import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, ReplaySubject } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { UserConfigs } from '@models/user-configs.model';
import { AuthenticationService } from './authentication.service';
import { Role } from '@models/role.model';
import { Store } from '@ngrx/store';
import { UserConfigsActions } from "@store/app.actions";

@Injectable({
	providedIn: 'root'
})
export class UserConfigsService {
	public get currentConfig(): UserConfigs {
		return this.cachedConfig;
	}

	private cachedConfig: UserConfigs;
	private userConfigs$ = new ReplaySubject<UserConfigs>(1);

	constructor(
		private http: HttpClient,
		private appConfigService: AppConfigService,
		private authenticationService: AuthenticationService,
		private store: Store
	) {
		this.authenticationService.getToken$()
			.subscribe(() => this.refreshUserConfigs());
	}

	public getUserConfigs(): Observable<UserConfigs> {
		return this.userConfigs$.asObservable();
	}

	public refreshUserConfigs(): void {
		if (!this.authenticationService.isLoggedIn() || this.authenticationService.shouldSignEula()) {
			return;
		}
		const url = `${this.appConfigService.getConfig().webApi2}/api2/UserConfigs`;
		this.http.get<UserConfigs>(url)
			.subscribe((uc: UserConfigs) => this.setUserConfigs(uc));
	}

	public inRoles(...params: Role[]): boolean {
		if (!this.cachedConfig) {
			return false;
		}

		return this.cachedConfig.userRoles.some(r => params.includes(r));
	}

	public inRoles$(...params: Role[]): Observable<boolean> {
		return this.userConfigs$.pipe(
			map((x) => {
				return x.userRoles.some(r => params.includes(r));
			})
		);
	}

	private setUserConfigs(userConfig: UserConfigs): void {
		if (this.cachedConfig && this.cachedConfig?.currentCase?.caseId !== userConfig?.currentCase?.caseId) {
			this.store.dispatch(UserConfigsActions.caseSwitched());
		}
		this.cachedConfig = userConfig;
		this.userConfigs$.next(userConfig);
	}
}
